import { useMutation } from '@tanstack/react-query';
import { Platform } from 'react-native';
import { usePathname } from 'expo-router';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/lib/supabase/auth';
import { mode } from '@/lib/config/app-mode';
import { getDeviceInfo, getAppVersion } from './device-info';
import type { FeedbackPayload, FeedbackInsert } from './types';
import type { Json } from '@/lib/supabase/types';

export { captureScreenshot } from './capture';

export function useFeedback() {
  const { user } = useAuth();
  const pathname = usePathname();

  return useMutation({
    mutationFn: async (payload: FeedbackPayload) => {
      if (!user) throw new Error('Not authenticated');

      // Mock mode — simulate success
      if (mode.showSampleData) {
        await new Promise((r) => setTimeout(r, 600));
        return { id: 'mock-feedback-id' };
      }

      let screenshotPath: string | null = null;

      // Upload screenshot if provided
      if (payload.screenshotUri) {
        const fileName = `${user.id}/${Date.now()}.jpg`;

        // On web, data-uri → blob; on native, fetch the tmpfile URI → blob
        const response = await fetch(payload.screenshotUri);
        const blob = await response.blob();

        const { error: uploadError } = await supabase.storage
          .from('feedback-screenshots')
          .upload(fileName, blob, { contentType: 'image/jpeg' });

        if (uploadError) {
          throw new Error(`Screenshot upload failed: ${uploadError.message}`);
        }

        // The bucket is PRIVATE (screenshots routinely capture PII and other
        // users' data), so we persist the storage PATH — not a URL. A public
        // URL would not resolve, and a signed URL would expire in the row.
        // Readers mint a short-lived signed URL at render time.
        screenshotPath = fileName;
      }

      const row: FeedbackInsert = {
        user_id: user.id,
        platform: Platform.OS,
        category: payload.category,
        text: payload.text,
        screenshot_path: screenshotPath,
        screen_context: pathname,
        app_version: getAppVersion(),
        device_info: getDeviceInfo(),
      };

      const { data, error } = await supabase
        .from('feedback')
        .insert({ ...row, device_info: row.device_info as unknown as Json })
        .select('id')
        .single();

      if (error) throw error;
      return data;
    },
  });
}
